import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["after", "appearance", "className", "children", "before", "density", "field", "intent", "isClearable", "isDisabled", "isRequired", "required", "name", "onBlur", "onChange", "onClear", "onUpdate", "onFocus", "readOnly", "shape", "size", "value"];
import * as React from 'react';
import cn from 'classnames';
import { intentClass, ControlSizeType, ControlAppearanceType, ControlShapeType, controlClasses, safeInvoke, isDefined } from '../../../../library';
import { Close } from '../../../close';
import { InputButton } from './input-button';
import { isNull } from 'lodash';
export var inputClasses = {
  MAIN: 'input',
  INPUT: 'control__text',
  ADDON: 'control__addon'
};
var TextInput = React.forwardRef(function (props, ref) {
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    isFocused = _React$useState2[0],
    setIsFocused = _React$useState2[1];
  var inputRef = React.useRef(null);
  var handleChange = function handleChange(e) {
    var value = e.currentTarget.value;
    safeInvoke(props.onUpdate, props.field, value, e);
    safeInvoke(props.onChange, e);
  };
  var handleBlur = function handleBlur(e) {
    setIsFocused(false);
    safeInvoke(props.onBlur, e);
  };
  var handleFocus = function handleFocus(e) {
    setIsFocused(true);
    safeInvoke(props.onFocus, e);
  };
  var handleContainerFocus = function handleContainerFocus(e) {
    var _inputRef$current;
    if (e.defaultPrevented || inputRef.current === document.activeElement) {
      return;
    }
    (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.focus();
  };
  var handleClear = function handleClear(e) {
    var _inputRef$current2;
    e.preventDefault();
    if (props.isDisabled) return;
    if (isDefined(props.value)) {
      safeInvoke(props.onUpdate, props.field, '');
    } else if (isDefined(inputRef.current)) {
      inputRef.current.value = '';
    }
    (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 || _inputRef$current2.focus();
    safeInvoke(props.onClear);
  };
  var renderAttachment = function renderAttachment(content) {
    var contentArray = Array.isArray(content) ? content : [content];
    return contentArray.map(function (content, i) {
      if (isNull(content)) return null;
      return React.createElement("div", {
        key: i,
        className: inputClasses.ADDON
      }, " ", content, " ");
    });
  };
  var renderClearButton = function renderClearButton() {
    var isControlled = props.value !== undefined;
    if (!props.isClearable || props.isDisabled || props.readOnly || isControlled && !props.value) return null;
    return React.createElement("div", {
      className: inputClasses.ADDON
    }, React.createElement(Close, {
      size: props.size,
      shape: props.shape,
      onClick: handleClear,
      onFocus: function onFocus(e) {
        e.stopPropagation();
      }
    }));
  };
  var after = props.after,
    appearance = props.appearance,
    className = props.className,
    children = props.children,
    before = props.before,
    density = props.density,
    field = props.field,
    intent = props.intent,
    isClearable = props.isClearable,
    isDisabled = props.isDisabled,
    isRequired = props.isRequired,
    required = props.required,
    name = props.name,
    onBlur = props.onBlur,
    onChange = props.onChange,
    onClear = props.onClear,
    onUpdate = props.onUpdate,
    onFocus = props.onFocus,
    readOnly = props.readOnly,
    shape = props.shape,
    size = props.size,
    value = props.value,
    htmlProps = _objectWithoutProperties(props, _excluded);
  var classNames = cn('input', {
    '-readonly': readOnly,
    '-disabled': isDisabled,
    '-focus': isFocused,
    '-required': isRequired || required,
    '-hidden': props.type === 'hidden'
  }, controlClasses({
    size: size,
    shape: shape,
    density: density,
    appearance: appearance
  }), intentClass(props.intent), props.className);
  return React.createElement("div", {
    className: classNames,
    ref: ref,
    onFocus: handleContainerFocus,
    tabIndex: -1
  }, !!before ? renderAttachment(before) : null, React.createElement("input", _extends({
    ref: inputRef,
    id: field,
    className: inputClasses.INPUT,
    onChange: handleChange,
    onBlur: handleBlur,
    onFocus: handleFocus,
    disabled: isDisabled,
    name: props.field,
    value: value !== null ? value : '',
    readOnly: readOnly
  }, htmlProps)), !!after ? renderAttachment(after) : null, renderClearButton());
});
TextInput.defaultProps = {
  shape: ControlShapeType.ROUNDED,
  size: ControlSizeType.MEDIUM,
  appearance: ControlAppearanceType.OUTLINE
};
TextInput.displayName = 'Input';
export var Input = Object.assign(TextInput, {
  Button: InputButton
});